import { useState } from "react";
import { Play } from "lucide-react";
import { Reveal, Eyebrow } from "./primitives";

const VIDEOS = [
  {
    id: "pldnIq6gTmA",
    title: "“Half of You” (2026 Live Version)",
    caption: "",
  },
  {
    id: "T82S28Hq6zM",
    title: "“Half of You” (2025 version)",
    caption: "Released September 2025.",
  },
  {
    id: "tCxSO8W-wB8",
    title: "“The Rage Stage”",
    caption: "Released Fall 2025.",
  },
  {
    id: "O_8Z0TuwcgY",
    title: "“Fragmented”",
    caption: "Released Fall 2025.",
  },
];

function Player({ id }) {
  const [playing, setPlaying] = useState(false);
  const thumb = `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-3xl border border-line shadow-[0_30px_80px_rgba(45,48,42,0.14)]">
      {playing ? (
        <iframe
          data-testid={`video-iframe-${id}`}
          className="absolute inset-0 h-full w-full"
          src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
          title="Featured video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          data-testid={`video-play-button-${id}`}
          aria-label="Play video"
          className="group absolute inset-0 h-full w-full"
        >
          <img
            src={thumb}
            alt="Video thumbnail"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={(e) => {
              e.currentTarget.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
            }}
          />
          <span className="absolute inset-0 bg-ink/30 transition-colors duration-300 group-hover:bg-ink/40" />
          <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-terracotta text-white shadow-[0_10px_40px_rgba(193,119,103,0.5)] transition-transform duration-300 group-hover:scale-110">
            <Play className="ml-1 h-8 w-8 fill-current" strokeWidth={1.5} />
          </span>
        </button>
      )}
    </div>
  );
}

export default function VideoFeature() {
  return (
    <section id="watch" data-testid="video-section" className="bg-bone py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal className="max-w-3xl">
          <Eyebrow>Watch & understand</Eyebrow>
          <h2 className="mt-6 font-serif text-4xl font-medium leading-tight tracking-tight text-ink sm:text-5xl">
            Learning to see the signs, and to love myself
          </h2>
          <p className="mt-6 text-base leading-relaxed text-stone sm:text-lg">
            For a long time, I didn't catch the signs. It was only later that I learned to love
            myself more and to educate myself about what had happened to me. Things eventually
            became so bad that I had to get a restraining order for sexual assault.
          </p>
          <p className="mt-4 text-base leading-relaxed text-stone sm:text-lg">
            Over several years of programs and support groups, and working alongside doulas, I came
            to understand how decisions made or forced around reproductive choices and pregnancy can
            have lasting effects. My poetry is part of that journey. Sometimes the first step
            toward safety is simply finding your voice again and knowing you are seen.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-2">
          {VIDEOS.map((v, i) => (
            <Reveal key={v.id} delay={i * 0.1}>
              <Player id={v.id} />
              <h3 className="mt-5 font-serif text-2xl font-medium text-ink">{v.title}</h3>
              {v.caption && <p className="mt-1 text-sm text-stone">{v.caption}</p>}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
